import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, type ArticleSection } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const articleDetailImages: Record<string, string> = {
  'phone-camera-comparison': 'https://readdy.ai/api/search-image?query=Multiple%20modern%20Chinese%20smartphones%20lined%20up%20showing%20camera%20interfaces%2C%20product%20comparison%20flat%20lay%2C%20warm%20minimalist%20studio%20lighting%2C%20clean%20cream%20background%2C%20professional%20tech%20product%20photography&width=800&height=400&seq=phone-camera-comparison&orientation=landscape',
  'rule-of-thirds-grid-demo': 'https://readdy.ai/api/search-image?query=Smartphone%20screen%20showing%20camera%20viewfinder%20with%20grid%20lines%20overlay%20for%20rule%20of%20thirds%20composition%2C%20photography%20tutorial%20visual%2C%20minimalist%20setup%2C%20warm%20ambient%20light%2C%20clean%20background&width=800&height=400&seq=rule-of-thirds-grid-demo&orientation=landscape',
  'golden-hour-selfie-demo': 'https://readdy.ai/api/search-image?query=Warm%20golden%20sunset%20light%20streaming%20through%20window%20creating%20beautiful%20rim%20light%20effect%2C%20photography%20lighting%20demonstration%2C%20soft%20cream%20interior%2C%20artistic%20atmosphere%20with%20dust%20particles%20in%20light&width=800&height=400&seq=golden-hour-selfie-demo&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <p className="text-foreground-500">文章不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const categoryName = getCategoryName(article.category);

  const headingIds: { id: string; text: string; level: number }[] = [];
  article.content.forEach((section) => {
    if (section.type === 'heading' && section.content) {
      const id = `section-${headingIds.length}`;
      headingIds.push({ id, text: section.content, level: section.level || 2 });
    }
  });

  const renderSection = (section: ArticleSection, idx: number) => {
    switch (section.type) {
      case 'heading': {
        const Tag = section.level === 3 ? 'h3' : 'h2';
        const headingId = `section-${headingIds.findIndex((h) => h.text === section.content)}`;
        return (
          <Tag
            id={headingId}
            className={`font-heading font-semibold text-foreground-950 mt-10 mb-4 ${
              section.level === 3 ? 'text-lg' : 'text-xl md:text-2xl'
            }`}
          >
            {section.content}
          </Tag>
        );
      }
      case 'paragraph':
        return (
          <p className="text-foreground-700 leading-relaxed mb-4 text-sm">
            {section.content}
          </p>
        );
      case 'image': {
        const imgKey = section.imageSeq || '';
        return (
          <figure className="my-6">
            <img
              src={articleDetailImages[imgKey] || articleDetailImages['phone-camera-comparison']}
              alt={section.imageAlt || ''}
              className="w-full rounded-xl"
            />
            {section.caption && (
              <figcaption className="text-center text-xs text-foreground-500 mt-2">
                {section.caption}
              </figcaption>
            )}
          </figure>
        );
      }
      case 'list':
        return (
          <ul className="mb-4 space-y-1.5">
            {section.items?.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      case 'tip':
        return (
          <div className="my-6 p-4 bg-accent-100/50 border border-accent-200/60 rounded-xl flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50 flex-shrink-0">
              <i className="ri-lightbulb-line text-sm"></i>
            </div>
            <div>
              <p className="text-xs font-semibold text-accent-800 mb-1">小贴士</p>
              <p className="text-sm text-accent-800/80 leading-relaxed">{section.content}</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ScrollToTop />
      <PageSEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.selfie-guide.cn/article/${slug}`}
        ogType="article"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          datePublished: article.date,
          url: `https://www.selfie-guide.cn/article/${slug}`,
          publisher: {
            '@type': 'Organization',
            name: '国产自拍完全指南',
          },
        }}
      />
      <Navbar />

      <main className="pt-20 min-h-screen">
        {/* Article Header */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mt-6 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读约{article.readTime}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-md text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Article Content */}
            <article className="flex-1 max-w-3xl">
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
                {article.content.map((section, idx) => (
                  <div key={idx}>{renderSection(section, idx)}</div>
                ))}
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div className="mt-10">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5">
                    相关文章推荐
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.slug}
                        to={`/article/${ra.slug}`}
                        className="group p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                      >
                        <h4 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-600 line-clamp-2">{ra.excerpt}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* Table of Contents Sidebar */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24">
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                  <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-4">
                    <a href="#toc" className="hover:text-primary-600 transition-colors">文章目录</a>
                  </h4>
                  <nav className="space-y-0.5" id="toc">
                    {headingIds.map((h) => (
                      <a
                        key={h.id}
                        href={`#${h.id}`}
                        className={`block text-xs py-1.5 transition-colors hover:text-primary-600 ${
                          h.level === 3 ? 'pl-4 text-foreground-500' : 'text-foreground-700 font-medium'
                        }`}
                      >
                        {h.text}
                      </a>
                    ))}
                  </nav>
                </div>

                <div className="mt-4 bg-background-50 rounded-xl border border-background-200/70 p-5">
                  <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-3">
                    <a href="#categories-side" className="hover:text-primary-600 transition-colors">分类导航</a>
                  </h4>
                  <nav className="space-y-1" id="categories-side">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2 text-xs py-1.5 transition-colors hover:text-primary-600 ${
                          cat.slug === article.category ? 'text-primary-600 font-medium' : 'text-foreground-600'
                        }`}
                      >
                        <i className={`${cat.icon} text-sm`}></i>
                        {cat.name}
                      </Link>
                    ))}
                  </nav>
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* BreadcrumbList Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.selfie-guide.cn/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: categoryName,
                  item: `https://www.selfie-guide.cn/category/${article.category}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                },
              ],
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}